// ============================================
// ShadowCaster2D — 2D shadow caster components
// ============================================
//
// Shadow casters define world-space line segments that block light.
// The ShadowMapPass collects all edges and traces rays from each
// shadow-casting light to compute 1D shadow maps.

// ============================================================================
// Shadow Caster Types
// ============================================================================

/**
 * A shadow caster defined by a set of world-space line segments (edges).
 * Each edge blocks light rays that cross it.
 */
export interface ShadowCaster2D {
  /** Whether this caster is active. */
  enabled: boolean;
  /** Shape type for the caster. */
  shape: ShadowCasterShape;
}

/** Union of supported shadow caster shape types. */
export type ShadowCasterShape =
  | ShadowBoxShape
  | ShadowCircleShape
  | ShadowPolygonShape
  | ShadowEdgesShape;

/**
 * Box shadow caster — 4 edges forming an axis-aligned or rotated rectangle.
 */
export interface ShadowBoxShape {
  readonly type: 'box';
  /** Center position in world space. */
  center: [number, number];
  /** Half-extents (halfWidth, halfHeight). */
  halfExtents: [number, number];
  /** Rotation in radians (default 0). */
  rotation: number;
}

/**
 * Circle shadow caster — approximated as an N-gon (16 edges by default).
 */
export interface ShadowCircleShape {
  readonly type: 'circle';
  /** Center position in world space. */
  center: [number, number];
  /** Radius in world units. */
  radius: number;
  /** Number of segments for the approximation (default 16). */
  segments: number;
}

/**
 * Polygon shadow caster — arbitrary convex or concave polygon from vertices.
 * Edges connect consecutive vertices, with the last vertex connecting to the first.
 */
export interface ShadowPolygonShape {
  readonly type: 'polygon';
  /** Vertices in world space: [x0, y0, x1, y1, ...]. */
  vertices: number[];
}

/**
 * Raw edges shadow caster — explicitly defined line segments.
 * Each edge is [startX, startY, endX, endY].
 */
export interface ShadowEdgesShape {
  readonly type: 'edges';
  /** Flat array: [x0, y0, x1, y1, x2, y2, x3, y3, ...] — 4 floats per edge. */
  edges: number[];
}

// ============================================================================
// Edge Extraction
// ============================================================================

/**
 * Edges are stored as flat float arrays: [startX, startY, endX, endY] per edge.
 * This is the format uploaded to the GPU storage buffer.
 */
export type ShadowEdgeArray = Float32Array;

/**
 * Extract world-space line-segment edges from a shadow caster shape.
 *
 * @returns Array of edges as [startX, startY, endX, endY] groups.
 */
export function extractEdges(shape: ShadowCasterShape): number[] {
  switch (shape.type) {
    case 'box':
      return extractBoxEdges(shape);
    case 'circle':
      return extractCircleEdges(shape);
    case 'polygon':
      return extractPolygonEdges(shape);
    case 'edges':
      return [...shape.edges];
  }
}

function extractBoxEdges(box: ShadowBoxShape): number[] {
  const cx = box.center[0];
  const cy = box.center[1];
  const hx = box.halfExtents[0];
  const hy = box.halfExtents[1];
  const cos = Math.cos(box.rotation);
  const sin = Math.sin(box.rotation);

  // Local corners: TL, TR, BR, BL
  const corners: [number, number][] = [
    [-hx, -hy],
    [hx, -hy],
    [hx, hy],
    [-hx, hy],
  ];

  // Rotate and translate to world space
  const world: [number, number][] = corners.map(([lx, ly]) => [
    cx + lx * cos - ly * sin,
    cy + lx * sin + ly * cos,
  ]);

  // 4 edges: TL→TR, TR→BR, BR→BL, BL→TL
  const edges: number[] = [];
  for (let i = 0; i < 4; i++) {
    const a = world[i];
    const b = world[(i + 1) % 4];
    edges.push(a[0], a[1], b[0], b[1]);
  }
  return edges;
}

function extractCircleEdges(circle: ShadowCircleShape): number[] {
  const cx = circle.center[0];
  const cy = circle.center[1];
  const r = circle.radius;
  const n = circle.segments;
  const edges: number[] = [];

  for (let i = 0; i < n; i++) {
    const a0 = (i / n) * Math.PI * 2;
    const a1 = ((i + 1) / n) * Math.PI * 2;
    edges.push(
      cx + Math.cos(a0) * r,
      cy + Math.sin(a0) * r,
      cx + Math.cos(a1) * r,
      cy + Math.sin(a1) * r,
    );
  }
  return edges;
}

function extractPolygonEdges(poly: ShadowPolygonShape): number[] {
  const verts = poly.vertices;
  const n = verts.length / 2;
  if (n < 2) return [];

  const edges: number[] = [];
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    edges.push(
      verts[i * 2], verts[i * 2 + 1],
      verts[j * 2], verts[j * 2 + 1],
    );
  }
  return edges;
}

// ============================================================================
// Factory Helpers
// ============================================================================

/** Create a box shadow caster. */
export function createShadowBox(
  center: [number, number],
  halfExtents: [number, number],
  rotation = 0,
): ShadowCaster2D {
  return {
    enabled: true,
    shape: { type: 'box', center, halfExtents, rotation },
  };
}

/** Create a circle shadow caster. */
export function createShadowCircle(
  center: [number, number],
  radius: number,
  segments = 16,
): ShadowCaster2D {
  return {
    enabled: true,
    shape: { type: 'circle', center, radius, segments },
  };
}

/** Create a polygon shadow caster from a list of vertices. */
export function createShadowPolygon(vertices: number[]): ShadowCaster2D {
  return {
    enabled: true,
    shape: { type: 'polygon', vertices: [...vertices] },
  };
}
